import { Flex, type FlexProps } from '@commons/containers/Flex';
import PageBuilder from '@commons/pagebuilder';
import type { DrupalBlock, DrupalSection } from '@commons/types/drupal';
import type { Alignment, Spacing } from '@commons/types/styles';

/**
 * SectionHeaderRegion component.
 */

const SectionHeaderRegion = (props: FlexProps) => {
  const { children, align = 'start', ref, ...rest } = props;

  return (
    <Flex ref={ref} align={align} direction="column" {...rest}>
      {children}
    </Flex>
  );
};

/**
 * Type definition of a Drupal section header payload.
 */

export type DrupalSectionHeader = {
  marginBottom: Spacing;
  horizontalAlignment: Alignment;
};

/**
 * Function to render the header region of a section.
 *
 * @param blocks - The blocks to render.
 * @param header - The header properties.
 * @param section - The section properties (used by Craft.js).
 * @param additionalProps - Additional properties to pass to the region component.
 *
 * @returns The rendered header region.
 */

export const renderHeaderRegion = (
  blocks: DrupalBlock[],
  header: DrupalSectionHeader,
  section: Partial<DrupalSection>,
  additionalProps?: FlexProps
) => {
  const { horizontalAlignment, marginBottom } = header;

  const renderedHeaderBlocks = blocks.map(PageBuilder.renderBlock);

  const props = {
    children: renderedHeaderBlocks,
    align: horizontalAlignment,
    marginBottom,
    ...additionalProps,
  };

  return PageBuilder.renderRegion('header', SectionHeaderRegion, props, section);
};
